<template>
  <div class="select-field" :class="{ active: active, 'has-error': !!errorMessage }">
    <div class="select-field-prepend" v-if="$slots.prepend">
      <slot name="prepend" />
    </div>

    <div class="select-field-inner">
      <label v-if="label" class="select-field-label" :class="{ visible: active || !!modelValue }">
        {{ label }}
      </label>
      <select
        ref="selectRef"
        class="select-field-select"
        :name="name"
        :value="modelValue"
        :required="required"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      >
        <option v-if="placeholder" value="">{{ placeholder }}</option>
        <option
          v-for="(option, i) in options"
          :key="i"
          :value="option.value !== undefined ? option.value : i"
          :selected="modelValue === (option.value !== undefined ? option.value : i)"
          :disabled="option.disabled"
          :style="option.disabled ? { display: 'none' } : ''"
          v-html="option.text !== undefined ? option.text : option"
        />
      </select>
    </div>

    <div class="select-field-append" v-if="errorMessage || $slots.append">
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
      <slot name="append" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  modelValue: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  options: {
    type: [Array, Object],
    default: () => []
  },
  placeholder: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  isValidationAttempt: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'onFocus', 'onBlur'])

const active = ref(false)
const selectRef = ref<HTMLSelectElement | null>(null)

const errorMessage = computed(() => {
  if (props.required && props.isValidationAttempt && !props.modelValue) {
    return `${props.label} is required.`
  }
  return ''
})

const onInput = (event: Event) => {
  const target = event.target as HTMLSelectElement
  emit('update:modelValue', target.value)
}

const onFocus = () => {
  active.value = true
  emit('onFocus', true)
}

const onBlur = () => {
  active.value = false
  emit('onBlur', true)
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/inc/variables';

.select-field {
  position: relative;
  width: 100%;
  margin-bottom: $spacer-3;
}

.select-field-inner {
  position: relative;
  background: transparent;
  border: 2px solid var(--color-border-primary);
  border-radius: $border-radius;
  height: 48px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  transition: $transition-base;

  .select-field.active & {
    border-color: var(--color-border-active);
  }

  .select-field.has-error & {
    border-color: $red50;
  }
}

.select-field-label {
  position: absolute;
  left: $spacer-2;
  top: -$spacer-2;
  background-color: var(--color-bg-primary);
  padding: 0 $spacer-1;
  color: var(--color-text-primary);
  font-family: $font-family-pilat-book;
  font-size: $fs_2;
  font-weight: $font-weight-normal;
  opacity: 0;
  transform: translateY($spacer-1);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  pointer-events: none;
  z-index: 1;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.select-field-select {
  width: 100%;
  border: none;
  background: transparent url('/app/themes/output/assets/svg/icons/arrow-down.svg') no-repeat right center;
  outline: none;
  color: var(--color-text-primary);
  font-size: $fs_1;
  font-weight: $font-weight-normal;
  font-family: $font-family-base;
  padding: 0;
  padding-right: $spacer-4;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -webkit-border-radius: 0;
  line-height: 1.5;

  &:autofill,
  &:autofill:hover,
  &:autofill:focus,
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--color-text-primary);
  }

  option {
    padding: 0;
    color: initial;
    background: initial;
  }
}

.select-field-prepend {
  margin-bottom: $spacer-2;
}

.select-field-append {
  margin-top: $spacer-2;
}

.error-message {
  color: $red50;
  font-size: $fs_3;
  margin-top: $spacer-1;
}
</style>
